import AiAssistantPanel from '@/components/ai/AiAssistantPanel';
import ContractInteractionPanel from '@/components/contracts/ContractInteractionPanel';
import { CreateDappWizard } from '@/components/dapp-builder';
import RpcDebuggerPanel from '@/components/debug/RpcDebuggerPanel';
import DeployPanel from '@/components/deploy/DeployPanel';
import EditorPanel from '@/components/editor/EditorPanel';
import FileExplorer from '@/components/explorer/FileExplorer';
import {
    ExtensionPanelRenderer,
    ExtensionsManagerPanel,
} from '@/components/extensions/ExtensionPanel';
import GitPanel from '@/components/git/GitPanel';
import AiGovernancePanel from '@/components/platform/AiGovernancePanel';
import LogsViewerPanel from '@/components/platform/LogsViewerPanel';
import SecretsEnvironmentPanel from '@/components/platform/SecretsEnvironmentPanel';
import LivePreviewPane from '@/components/preview/LivePreviewPane';
import QuickStartWizard from '@/components/quickstart/QuickStartWizard';
import SearchPanel from '@/components/search/SearchPanel';
import SettingsPanel from '@/components/settings/SettingsPanel';
import Terminal from '@/components/terminal/Terminal';
import CommandPalette, { buildIDECommands } from '@/components/ui/CommandPalette';
import ExtensionDialog from '@/components/ui/ExtensionDialog';
import KeyboardShortcutsDialog from '@/components/ui/KeyboardShortcutsDialog';
import QuickFileOpen from '@/components/ui/QuickFileOpen';
import ResizeHandle from '@/components/ui/ResizeHandle';
import StatusBar from '@/components/ui/StatusBar';
import WalletPanel from '@/components/wallet/WalletPanel';
import WorkspaceHealthPanel from '@/components/workspace/WorkspaceHealthPanel';
import { useAiContext } from '@/hooks/useAiContext';
import { useKeyboardShortcuts, type KeyBinding } from '@/hooks/useKeyboardShortcuts';
import * as fileService from '@/services/file.service';
import {
    useAiAssistantStore,
    useDeployStore,
    useTerminalStore,
    useUIStore,
    useWorkspaceStore,
} from '@/store';
import { useEditorStore } from '@/store/editorStore';
import { useExtensionStore } from '@/store/extensionStore';
import { useCallback, useMemo, useState } from 'react';
import ActivityBar from './ActivityBar';

export default function IDELayout() {
  const {
    sidebarWidth,
    terminalHeight,
    showPreview,
    showTerminal,
    activePanel,
    setSidebarWidth,
    setTerminalHeight,
    setShowTerminal,
    setShowPreview,
    setActivePanel,
  } = useUIStore();

  const { activeWorkspace } = useWorkspaceStore();
  const { compile } = useDeployStore();
  const { sendMessage } = useAiAssistantStore();
  const { clearMessages: clearTerminalMessages } = useTerminalStore();
  const { activeTabId, tabs, saveFile, fileTree, openFile } = useEditorStore();
  const extensionPanels = useExtensionStore((s) => s.panels);
  const workspaceId = activeWorkspace?.id ?? '';

  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showQuickOpen, setShowQuickOpen] = useState(false);
  const [showQuickStart, setShowQuickStart] = useState(false);

  const { getAiContext } = useAiContext();

  const getActiveFileContext = useCallback((): Record<string, unknown> => {
    return getAiContext();
  }, [getAiContext]);

  const commands = useMemo(() => {
    return buildIDECommands({
      compile: () => {
        compile(workspaceId);
      },
      saveFile: () => {
        const tab = tabs.find((t) => t.id === activeTabId);
        if (tab) saveFile(tab.path);
      },
      toggleTerminal: () => setShowTerminal(!showTerminal),
      togglePreview: () => setShowPreview(!showPreview),
      openExplorer: () => setActivePanel('explorer'),
      openDeploy: () => setActivePanel('deploy'),
      openAI: () => setActivePanel('ai'),
      openSettings: () => setActivePanel('settings'),
      clearTerminal: () => clearTerminalMessages(),
      aiGenerate: () => {
        setActivePanel('ai');
        sendMessage(
          'Generate a smart contract based on my open file',
          'contract_template',
          getActiveFileContext(),
        );
      },
      aiExplain: () => {
        setActivePanel('ai');
        sendMessage('Explain the current code in detail', 'code_explain', getActiveFileContext());
      },
      aiAudit: () => {
        setActivePanel('ai');
        sendMessage('Run a security audit on this code', 'security_audit', getActiveFileContext());
      },
      aiGasOptimize: () => {
        setActivePanel('ai');
        sendMessage(
          'Analyze gas usage and suggest optimizations',
          'gas_optimize',
          getActiveFileContext(),
        );
      },
      aiGenerateTests: () => {
        setActivePanel('ai');
        sendMessage(
          'Generate comprehensive tests for this contract',
          'test_generate',
          getActiveFileContext(),
        );
      },
    });
  }, [
    activeTabId,
    tabs,
    workspaceId,
    showTerminal,
    showPreview,
    compile,
    saveFile,
    setShowTerminal,
    setShowPreview,
    setActivePanel,
    clearTerminalMessages,
    sendMessage,
    getActiveFileContext,
  ]);

  const handleSidebarResize = useCallback(
    (delta: number) => setSidebarWidth(sidebarWidth + delta),
    [sidebarWidth, setSidebarWidth],
  );

  const handleTerminalResize = useCallback(
    (delta: number) => setTerminalHeight(terminalHeight - delta),
    [terminalHeight, setTerminalHeight],
  );

  // ---- Comprehensive keyboard shortcuts ----
  const shortcuts: KeyBinding[] = useMemo(
    () => [
      { label: 'Command Palette', keys: 'ctrl+shift+p', action: () => setShowCommandPalette(true) },
      { label: 'Quick Open', keys: 'ctrl+p', action: () => setShowQuickOpen(true) },
      {
        label: 'Save File',
        keys: 'ctrl+s',
        action: () => {
          const tab = tabs.find((t) => t.id === activeTabId);
          if (tab) saveFile(tab.path);
        },
      },
      { label: 'Toggle Terminal', keys: 'ctrl+`', action: () => setShowTerminal(!showTerminal) },
      {
        label: 'Toggle Sidebar',
        keys: 'ctrl+b',
        action: () => setSidebarWidth(sidebarWidth > 50 ? 0 : 260),
      },
      { label: 'Explorer', keys: 'ctrl+shift+e', action: () => setActivePanel('explorer') },
      { label: 'Search Files', keys: 'ctrl+shift+f', action: () => setActivePanel('search') },
      { label: 'Source Control', keys: 'ctrl+shift+g', action: () => setActivePanel('git') },
      { label: 'Deploy Panel', keys: 'ctrl+shift+d', action: () => setActivePanel('deploy') },
      { label: 'AI Assistant', keys: 'ctrl+shift+a', action: () => setActivePanel('ai') },
      { label: 'Health Monitor', keys: 'ctrl+shift+h', action: () => setActivePanel('health') },
      {
        label: 'Compile',
        keys: 'ctrl+shift+b',
        action: () => {
          compile(workspaceId);
        },
      },
      { label: 'Toggle Preview', keys: 'ctrl+shift+v', action: () => setShowPreview(!showPreview) },
      { label: 'Quick Start', keys: 'ctrl+shift+n', action: () => setShowQuickStart(true) },
      { label: 'Keyboard Shortcuts', keys: 'ctrl+shift+k', action: () => setShowShortcuts(true) },
    ],
    [
      activeTabId,
      tabs,
      showTerminal,
      showPreview,
      sidebarWidth,
      workspaceId,
      compile,
      saveFile,
      setShowTerminal,
      setShowPreview,
      setActivePanel,
      setSidebarWidth,
    ],
  );

  useKeyboardShortcuts(shortcuts);

  return (
    <div className="flex flex-col h-screen w-screen bg-[#1e1e1e] text-white overflow-hidden">
      {/* Main content area */}
      <div className="flex flex-1 min-h-0">
        {/* Activity bar */}
        <ActivityBar />

        {/* Sidebar */}
        <div
          className="flex flex-col bg-[#252526] border-r border-[#1e1e1e] overflow-hidden shrink-0"
          style={{ width: sidebarWidth }}
        >
          {/* Sidebar header */}
          <div className="flex items-center h-9 px-4 text-[11px] font-semibold uppercase tracking-wider text-[#bbbbbb] border-b border-[#1e1e1e] select-none shrink-0">
            {activePanel === 'explorer' && 'Explorer'}
            {activePanel === 'search' && 'Search'}
            {activePanel === 'git' && 'Source Control'}
            {activePanel === 'deploy' && 'Deploy'}
            {activePanel === 'ai' && 'AI Assistant'}
            {activePanel === 'debug' && 'RPC Debugger'}
            {activePanel === 'health' && 'Health Monitor'}
            {activePanel === 'wallet' && 'Wallet'}
            {activePanel === 'contracts' && 'Contracts'}
            {activePanel === 'ai-agent' && 'AI Agent'}
            {activePanel === 'secrets' && 'Secrets & Env'}
            {activePanel === 'logs' && 'Logs'}
            {activePanel === 'extensions' && 'Extensions'}
            {activePanel === 'settings' && 'Settings'}
            {typeof activePanel === 'string' &&
              activePanel.startsWith('ext:') &&
              (extensionPanels.find((p) => `ext:${p.extensionId}:${p.id}` === activePanel)?.label ??
                'Extension')}
          </div>

          {/* Sidebar content */}
          <div className="flex-1 overflow-y-auto">
            {activePanel === 'explorer' && <FileExplorer workspaceId={workspaceId} />}
            {activePanel === 'search' && <SearchPanel workspaceId={workspaceId} />}
            {activePanel === 'git' && <GitPanel workspaceId={workspaceId} />}
            {activePanel === 'deploy' && <DeployPanel workspaceId={workspaceId} />}
            {activePanel === 'ai' && <AiAssistantPanel />}
            {activePanel === 'debug' && (
              <RpcDebuggerPanel rpcEndpoint={activeWorkspace?.rpcEndpoint} />
            )}
            {activePanel === 'health' && (
              <WorkspaceHealthPanel
                workspaceId={workspaceId}
                containerId={activeWorkspace?.containerId}
              />
            )}
            {activePanel === 'wallet' && <WalletPanel />}
            {activePanel === 'contracts' && <ContractInteractionPanel />}
            {activePanel === 'ai-agent' && <AiGovernancePanel />}
            {activePanel === 'secrets' && <SecretsEnvironmentPanel />}
            {activePanel === 'logs' && <LogsViewerPanel />}
            {activePanel === 'settings' && <SettingsPanel />}
            {activePanel === 'extensions' && <ExtensionsManagerPanel />}
            {typeof activePanel === 'string' && activePanel.startsWith('ext:') && (
              <ExtensionPanelRenderer panelId={activePanel} workspaceId={workspaceId} />
            )}
          </div>
        </div>

        {/* Sidebar resize handle */}
        <ResizeHandle direction="horizontal" onResize={handleSidebarResize} />

        {/* Center area: Editor + Terminal */}
        <div className="flex flex-col flex-1 min-w-0">
          {/* Editor */}
          <div className="flex-1 min-h-0">
            <EditorPanel workspaceId={workspaceId} onQuickStart={() => setShowQuickStart(true)} />
          </div>

          {/* Terminal resize + terminal */}
          {showTerminal && (
            <>
              <ResizeHandle direction="vertical" onResize={handleTerminalResize} />
              <div
                className="shrink-0 border-t border-[#1e1e1e]"
                style={{ height: terminalHeight }}
              >
                <Terminal workspaceId={workspaceId} />
              </div>
            </>
          )}
        </div>

        {/* Preview panel */}
        {showPreview && (
          <>
            <ResizeHandle direction="horizontal" onResize={() => {}} />
            <div className="w-[400px] shrink-0 border-l border-[#1e1e1e]">
              {activeWorkspace?.id ? (
                <LivePreviewPane workspaceId={activeWorkspace.id} className="h-full" />
              ) : (
                <div className="flex h-full items-center justify-center text-xs text-[#858585]">
                  No active workspace
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Status bar */}
      <StatusBar />

      {/* Command Palette */}
      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        commands={commands}
      />

      {/* Quick File Open */}
      <QuickFileOpen
        isOpen={showQuickOpen}
        onClose={() => setShowQuickOpen(false)}
        fileTree={fileTree}
        onSelect={async (path) => {
          try {
            const content = await fileService.getFileContent(workspaceId, path);
            openFile(path, content);
          } catch {
            openFile(path, '');
          }
        }}
      />

      {/* Keyboard Shortcuts Dialog */}
      <KeyboardShortcutsDialog isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />

      {/* Quick Start Wizard */}
      <QuickStartWizard isOpen={showQuickStart} onClose={() => setShowQuickStart(false)} />

      {/* dApp Builder Wizard (gated by feature flag) */}
      {import.meta.env.VITE_DAPP_BUILDER_ENABLED !== 'false' && <CreateDappWizard />}

      {/* Extension Dialog */}
      <ExtensionDialog />
    </div>
  );
}
